import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Pin, ChevronRight, CalendarClock, Loader2 } from "lucide-react";

const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleString("en-AU", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "";

export default function PinnedCompetitions() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/me/pins");
        const names = data.pinned || [];
        if (names.length === 0) {
          if (alive) { setItems([]); setLoading(false); }
          return;
        }
        const results = await Promise.all(
          names.map(async (name) => {
            try {
              const r = await api.get(`/tournaments/${encodeURIComponent(name)}/fixtures`);
              return { name, next: (r.data.fixtures || [])[0] || null };
            } catch {
              return { name, next: null };
            }
          })
        );
        if (alive) setItems(results);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  if (loading)
    return (
      <div className="h-16 flex items-center justify-center" data-testid="pinned-loading">
        <Loader2 className="h-5 w-5 animate-spin text-primary" />
      </div>
    );
  if (items.length === 0) return null;

  return (
    <div className="space-y-2" data-testid="pinned-competitions">
      <div className="flex items-center gap-1.5 text-sm font-semibold text-muted-foreground">
        <Pin className="h-4 w-4 text-primary" /> Pinned competitions
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {items.map((it) => (
          <button
            key={it.name}
            onClick={() => navigate(`/tournaments/${encodeURIComponent(it.name)}`)}
            data-testid="pinned-card"
            className="glass rounded-2xl p-4 text-left flex items-center gap-3 hover:-translate-y-0.5 transition-transform"
          >
            <div className="min-w-0 flex-1">
              <div className="font-display font-bold truncate">{it.name}</div>
              {it.next ? (
                <div className="text-xs text-muted-foreground mt-0.5 truncate">
                  <span className="font-medium text-foreground">
                    {it.next.home.name} v {it.next.away.name}
                  </span>
                </div>
              ) : (
                <div className="text-xs text-muted-foreground mt-0.5">No upcoming fixtures</div>
              )}
              {it.next && (
                <div className="text-[11px] text-muted-foreground flex items-center gap-1 mt-0.5">
                  <CalendarClock className="h-3 w-3" /> {fmtDate(it.next.date)}
                </div>
              )}
            </div>
            <span className="flex items-center gap-1 text-xs font-semibold text-primary bg-primary/15 rounded-full px-2.5 py-1 shrink-0">
              Open <ChevronRight className="h-3.5 w-3.5" />
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}
